// Tag picker row for bulk-editing project or context on multiple tasks.
import { Box, Button, Autocomplete, Chip, TextField } from '@mui/material';
import TagChip from '@/components/ui/TagChip';
import { translate as t } from '@/i18n/useT';
import { autocompleteTextFieldSx } from '@/features/tasks/components/forms/taskFormStyles';
import { BulkEditFieldGroup } from './BulkEditFieldGroup';

/**
 * @param {{
 *   kind: 'project'|'context',
 *   label: string,
 *   value: unknown,
 *   onChange: (value: unknown) => void,
 *   options: string[],
 *   displayMap: Map<string, string>,
 *   untouchedSymbol: symbol,
 * }} props
 */
export const BulkEditTagField = ({
  kind,
  label,
  value,
  onChange,
  options,
  displayMap,
  untouchedSymbol,
}) => {
  const untouched = value === untouchedSymbol;
  const cleared = value === '';
  const selectedId = !untouched && !cleared ? String(value) : null;
  const displayLabel = selectedId
    ? (displayMap.get(selectedId.toLowerCase()) ?? selectedId)
    : '';

  const placeholder =
    kind === 'project'
      ? t('tasktagfields.projectPlaceholder', 'Add or select a project')
      : t('tasktagfields.contextsPlaceholder', 'Add or select contexts');

  const clearLabel =
    kind === 'project'
      ? t('taskbulkeditmodal.clearProject', 'Clear project')
      : t('taskbulkeditmodal.clearContext', 'Clear context');

  const autocompleteId = `bulk-edit-${kind}-autocomplete`;

  return (
    <BulkEditFieldGroup label={label} htmlFor={autocompleteId}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          alignItems: 'center',
          mb: 1,
        }}
      >
        {untouched ? (
          <Chip
            size="small"
            variant="outlined"
            label={t('taskbulkeditmodal.noChange', "Don't change")}
            data-testid={`bulk-edit-${kind}-unchanged`}
          />
        ) : null}
        {cleared ? (
          <Chip
            size="small"
            variant="outlined"
            label={clearLabel}
            onDelete={() => onChange(untouchedSymbol)}
            data-testid={`bulk-edit-${kind}-cleared`}
          />
        ) : null}
        {selectedId ? (
          <TagChip
            tag={kind === 'context' ? `@${selectedId}` : selectedId}
            label={displayLabel}
            onDelete={() => onChange(untouchedSymbol)}
            data-testid={`bulk-edit-${kind}-chip`}
          />
        ) : null}
        {!untouched ? (
          <Button size="small" onClick={() => onChange(untouchedSymbol)}>
            {t('taskbulkeditmodal.resetField', 'Reset')}
          </Button>
        ) : null}
        {untouched ? (
          <Button size="small" variant="outlined" onClick={() => onChange('')}>
            {t('taskbulkeditmodal.clearFieldShort', 'Clear')}
          </Button>
        ) : null}
      </Box>
      <Autocomplete
        freeSolo
        size="small"
        options={options}
        value={selectedId}
        onChange={(_, option) => {
          if (option) onChange(String(option));
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            id={autocompleteId}
            variant="outlined"
            placeholder={placeholder}
            sx={autocompleteTextFieldSx}
            inputProps={{ ...params.inputProps, id: autocompleteId }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              const input = /** @type {HTMLInputElement|null} */ (e.target);
              const next = input?.value?.trim();
              if (next) {
                e.preventDefault();
                onChange(next);
              }
            }}
          />
        )}
      />
    </BulkEditFieldGroup>
  );
};
